import { catchError, map, Observable, of, tap, switchMap, startWith } from 'rxjs';
import { ComponentDataSourceService } from '../../component-data-source.service';
import { ModuleVersionDTO } from '../../../../dto/module/module-version.dto';
import { LoadDataParams } from '../../../../interfaces/DataSource/DataSourceParams.interface';
import { FunctionRegistryService } from '../../../function-registry/function-registry.service';
import { environment } from '../../../../environment/environment';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';

export function moduleVersions(componentDataSourceService: ComponentDataSourceService, functionRegistryService: FunctionRegistryService) {
  // Get the data sharing service from the component data source service
  const dataSharingService = componentDataSourceService.injector.get(DataSharingService);
  return {
    name: 'ModuleVersions',
    description: 'Lists all versions of a specific module',
    execute: (params?: LoadDataParams) => {
      
      // Use reactive data sharing - this will automatically update when module data changes
      return dataSharingService.getData$('selected_module').pipe(
        startWith(dataSharingService.getData('selected_module')), // Start with current value
        switchMap(moduleData => {
          
          if (!moduleData?.module_id) {
            console.error('[moduleVersions] No module data found in data sharing service');
            console.error('[moduleVersions] Available keys in data sharing service:', dataSharingService.getAllKeys());
            return of({ items: [], totalCount: 0 });
          }

          
          return componentDataSourceService.http.get<any>(
            `${environment.apiUrl}/events/modules/${moduleData.module_id}/versions`,
            params?.queryParams ? { params: params.queryParams } : {}
          );
        }),
        tap(result => {
          if (result?.items) {
          }
        }),
        catchError(error => {
          console.error('[moduleVersions] Error fetching module versions:', error);
          if (error.error) {
            console.error('[moduleVersions] Error response body:', error.error);
          }
          return of({ items: [], totalCount: 0 });
        }),
        map(result => {
          
          // Handle different response formats
          let items: any[] = [];
          let totalCount = 0;
          let pageSize = 10;
          let pageIndex = 0;
          
          if (Array.isArray(result)) {
            // Direct array response
            items = result;
            totalCount = result.length;
          } else if (result && result.items && Array.isArray(result.items)) {
            // Standard response with items property
            items = result.items;
            totalCount = result.totalCount || result.items.length;
            pageSize = result.pageSize || 10;
            pageIndex = result.pageIndex || 0;
          } else if (result && result.data && Array.isArray(result.data)) {
            // Response with data property
            items = result.data;
            totalCount = result.totalCount || result.data.length;
            pageSize = result.pageSize || 10;
            pageIndex = result.pageIndex || 0;
          } else {
            console.warn('[moduleVersions] Unexpected response format:', result);
            return { items: [], totalCount: 0 };
          }
          
          if (items.length === 0) {
            return { items: [], totalCount: 0 };
          }
          
          try {
            const versions = items.map((version: any) => {
              
              const versionDTO: Partial<ModuleVersionDTO> = {
                version_id: version.version_id,
                module_id: version.module_id,
                valid_from: version.valid_from || version.version_start_date,
                valid_to: version.valid_to || version.version_end_date,
                maintenance_minutes: version.maintenance_minutes || 0,
                name: version.name || '',
                descriptions: version.descriptions || {},
                visibility: version.visibility || 'public'
              };
              
              // Format the date range
              
              const fromDate = versionDTO.valid_from ? new Date(versionDTO.valid_from).toLocaleDateString('da-DK').replace(/\./g, '/') : 'Ikke angivet';
              const toDate = versionDTO.valid_to ? new Date(versionDTO.valid_to).toLocaleDateString('da-DK').replace(/\./g, '/') : 'Pågående';
              const dateRange = `${fromDate} - ${toDate}`;
              
              
              // Format the description - use the same logic as modules service
              const descriptions = versionDTO.descriptions || {};
              
              let danishDesc = '';
              
              if (descriptions && typeof descriptions === 'object') {
                // Check if it's the same structure as modules (object with language codes as keys)
                if (descriptions['da'] && typeof descriptions['da'] === 'object' && descriptions['da'].translation_text) {
                  danishDesc = descriptions['da'].translation_text;
                } else if (descriptions['da'] && typeof descriptions['da'] === 'string') {
                  danishDesc = descriptions['da'];
                } else if (Array.isArray(descriptions)) {
                  // Handle array format from paneldynamic
                  const danishTranslation = descriptions.find((desc: any) => 
                    desc.language_code === 'da' || desc.language_code === 'dk' || desc.language_code === '0'
                  );
                  danishDesc = danishTranslation?.translation_text || '';
                } else {
                  // Try other possible keys
                  const descObj = descriptions as any;
                  danishDesc = descObj['da'] || descObj['dk'] || descObj['danish'] || descObj['description'] || '';
                }
              } else if (typeof descriptions === 'string') {
                danishDesc = descriptions;
              }
              
              const truncatedDesc = danishDesc.length > 80 ? danishDesc.substring(0, 77) + '...' : danishDesc;
              
              
              const mappedResult = {
                ...versionDTO,
                dateRange: dateRange,
                description: truncatedDesc,
                requirementCount: version.requirements?.length || 0,
                maintenanceCount: version.maintenance?.length || 0
              };
              return mappedResult;
            });

            return {
              items: versions,
              totalCount,
              pageSize,
              pageIndex
            };
          } catch (error) {
            console.error('[moduleVersions] Error in mapping process:', error);
            return { items: [], totalCount: 0 };
          }
        })
      );
    },
    settings: {
      columns: [
        { 
          key: 'dateRange', 
          header: 'Gyldig periode', 
          type: 'text'
        },
        { key: 'maintenance_minutes', header: 'Vedligeholdelse (min)', type: 'number' },
        { 
          key: 'description', 
          header: 'Beskrivelse', 
          type: 'text'
        },
        {
          key: 'actions',
          header: '',
          type: 'button',
          buttonText: 'Rediger',
          buttonColor: 'primary',
          variant: 'raised',
          action: (row: any) => {
            // Set the module_id in data sharing service for the modal
            dataSharingService.setData('editing_module_version', {
              version_id: row.version_id,
              module_id: row.module_id
            });
            // Don't pass the old row data - let SurveyJS load fresh data from module_data data source
            functionRegistryService.executeFunction('openModal', 'edit_module_version', {});
          }
        }
      ],
      pageable: true,
      sortable: true,
      rowHover: true,
      defaultSortColumn: 'dateRange',
      defaultSortDirection: 'desc'
    }
  }
} 